/*
 * FUROMA bundle upsell (v4.1) — <furoma-bundle> custom element.
 *
 * Two jobs:
 *  1. Cube scent selector. Radios (one per cube variant) drive the card: on change
 *     it swaps the cube photo, the lede (matched ↔ fact-free mixed), recomputes the
 *     price row + CTA, and re-gates the free-shipping line — all client-side, no
 *     network. The scent-matched radio is preselected in Liquid, so zero interaction
 *     reproduces the matched card.
 *  2. Add-to-cart. The viewed reed + the SELECTED cube variant go in as two ordinary
 *     line items via one /cart/add.js call. The −$5 is an automatic product-level
 *     Buy X Get Y discount in the admin, not applied here — so any cube variant keeps
 *     the price, and adding the two separately yields the same total.
 *
 * Cart rendering is handed back to Dawn's cart-notification / cart-drawer. Those
 * expect the single-item add response (top-level `key`); a multi-item response
 * carries `items`, so we pass the first item's key through — the notification shows
 * the reed, the bubble counts both.
 *
 * US-only store (ships_to_countries: ["US"], USD) — formatting "$" client-side is
 * safe here; it mirrors the money_without_trailing_zeros the server rendered.
 */
if (!customElements.get('furoma-bundle')) {
  customElements.define(
    'furoma-bundle',
    class FuromaBundle extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('[data-fup-add]');
        if (!this.button) return;

        this.errorEl = this.querySelector('[data-fup-error]');
        this.ledeEl = this.querySelector('[data-fup-lede]');
        this.cubePhoto = this.querySelector('[data-fup-cube-photo] img');
        this.wasEl = this.querySelector('[data-fup-was]');
        this.nowEl = this.querySelector('[data-fup-now]');
        this.ctaPriceEls = this.querySelectorAll('[data-fup-cta-price], [data-fup-cta-price-short]');
        // The non-gift perk is the shipping line; re-gated on price change.
        this.shipPerk = this.querySelector('.fup__perk:not(.fup__perk--gift)');

        this.mainPrice = parseInt(this.dataset.mainPrice, 10) || 0;
        this.save = parseInt(this.dataset.save, 10) || 0;
        this.shipThreshold = parseInt(this.dataset.shipThreshold, 10) || 0;
        this.matchedLine = this.dataset.matchedLine || '';
        this.ledeMatched = this.dataset.ledeMatched || '';
        this.ledeMixed = this.dataset.ledeMixed || '';

        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.onChange = this.onChange.bind(this);
        this.onClick = this.onClick.bind(this);
        this.addEventListener('change', this.onChange);
        this.button.addEventListener('click', this.onClick);

        if (document.querySelector('cart-drawer')) this.button.setAttribute('aria-haspopup', 'dialog');

        // One-time view event when the card scrolls into view (a real impression,
        // not just a render). The custom pixel forwards it to GA4.
        this._viewed = false;
        if ('IntersectionObserver' in window) {
          this._io = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting && !this._viewed) {
                  this._viewed = true;
                  this.track('bundle_view', this.eventData());
                  this._io.disconnect();
                }
              });
            },
            { threshold: 0.5 }
          );
          this._io.observe(this);
        }
      }

      disconnectedCallback() {
        if (this.button) this.button.removeEventListener('click', this.onClick);
        this.removeEventListener('change', this.onChange);
        if (this._io) this._io.disconnect();
      }

      get selected() {
        return this.querySelector('input[type="radio"]:checked');
      }

      /* The viewed product's CURRENT variant. On the Discovery Set PDP the customer
         picks 3-of-5 via the U23 scent picker, which drives the main product form's
         hidden [name="id"]; read it live so we add the chosen combination, not the
         page's default. Falls back to the Liquid-rendered id (reed cards: one variant,
         the two are identical). */
      liveMainId() {
        const form = document.getElementById('product-form-' + this.dataset.section);
        const input = form && form.querySelector('[name="id"]');
        const v = input && parseInt(input.value, 10);
        return v || parseInt(this.dataset.mainId, 10) || null;
      }

      get items() {
        const cube = this.selected;
        const mainId = this.liveMainId();
        if (!cube || !mainId) return [];
        return [
          { id: mainId, quantity: 1 },
          { id: parseInt(cube.dataset.variantId, 10), quantity: 1 },
        ];
      }

      /* money_without_trailing_zeros parity for USD */
      money(cents) {
        const n = cents / 100;
        return '$' + (cents % 100 === 0 ? String(n) : n.toFixed(2));
      }

      onChange(event) {
        if (!event.target.matches('input[type="radio"]')) return;
        const chip = event.target;
        this.showError('');

        const cubePrice = parseInt(chip.dataset.price, 10) || 0;
        const was = this.mainPrice + cubePrice;
        const now = was - this.save;

        if (this.wasEl) this.setMoney(this.wasEl, was);
        if (this.nowEl) this.setMoney(this.nowEl, now);
        this.ctaPriceEls.forEach((el) => (el.textContent = this.money(now)));

        // Photo: swaps only when Admin has assigned a per-variant image; today all
        // five cube variants share the generic shot, so this is a no-op until then.
        if (this.cubePhoto && chip.dataset.image) this.cubePhoto.src = chip.dataset.image;

        // Lede: matched string (names the reed's scent) only while the cube matches.
        if (this.ledeEl) {
          const matched = chip.dataset.scent === this.matchedLine;
          this.ledeEl.textContent = matched ? this.ledeMatched : this.ledeMixed;
        }

        // Re-gate the shipping line if a divergent price drops the bundle under
        // the threshold (all $22 today, so dormant — but honest if that changes).
        if (this.shipPerk) this.shipPerk.hidden = !(this.shipThreshold > 0 && now >= this.shipThreshold);
      }

      /* Swap only the money text, preserving the server-rendered SR-only prefix
         span (its text is a translation string JS doesn't have). */
      setMoney(el, cents) {
        const hidden = el.querySelector('.visually-hidden');
        Array.from(el.childNodes).forEach((n) => {
          if (n !== hidden) n.remove();
        });
        el.append(this.money(cents));
      }

      onClick(event) {
        event.preventDefault();
        if (this.button.getAttribute('aria-disabled') === 'true') return;

        const items = this.items;
        if (!items.length) return;

        this.setBusy(true);
        this.showError('');

        const body = { items };
        if (this.cart) {
          body.sections = this.cart.getSectionsToRender().map((s) => s.id);
          body.sections_url = window.location.pathname;
          this.cart.setActiveElement(document.activeElement);
        }

        const config = fetchConfig('javascript');
        config.body = JSON.stringify(body);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.showError(response.description || response.message);
              this.publishEvent('cartError', {
                errors: response.errors || response.description,
                message: response.message,
              });
              return;
            }
            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }
            const added = response.items || [];
            this.cart.renderContents(Object.assign({}, response, { key: added.length ? added[0].key : undefined }));
            this.publishEvent('cartUpdate', { cartData: response });
            this.track('bundle_add', this.eventData());
          })
          .catch((error) => {
            console.error('[furoma-bundle] add to cart failed', error);
            this.showError((window.cartStrings && window.cartStrings.error) || '');
          })
          .finally(() => this.setBusy(false));
      }

      publishEvent(name, detail) {
        if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
        publish(PUB_SUB_EVENTS[name], Object.assign({ source: 'furoma-bundle' }, detail));
      }

      /* Snapshot of the current selection for analytics. */
      eventData() {
        const cube = this.selected;
        const cubePrice = cube ? parseInt(cube.dataset.price, 10) || 0 : 0;
        return {
          template: this.dataset.section || '',
          cube_variant_id: cube ? cube.dataset.variantId : '',
          cube_scent: cube ? cube.dataset.scent : '',
          value: (this.mainPrice + cubePrice - this.save) / 100,
          currency: 'USD',
        };
      }

      /* Emit a custom Web Pixels event (furoma: prefix); a custom pixel in
         Admin → Customer events forwards it to GA4. No-op pre-analytics. */
      track(name, data) {
        try {
          if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
            Shopify.analytics.publish('furoma:' + name, data || {});
          }
        } catch (e) {}
      }

      setBusy(busy) {
        this.button.setAttribute('aria-busy', busy ? 'true' : 'false');
        this.button.setAttribute('aria-disabled', busy ? 'true' : 'false');
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message || '';
        this.errorEl.hidden = !message;
      }
    }
  );
}
